import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { keys } from "@/app/queryClient";
import { api } from "@/lib/api-client";
import { toast } from "@/components/ui/toast-store";
import type {
  CreateDepartmentInput,
  Department,
  Page,
  UpdateDepartmentInput,
} from "@/lib/api-types";

export interface DepartmentFilters {
  /** The company selection from the header switcher; omit for "all". */
  companyId?: string | undefined;
  search?: string;
  isActive?: boolean;
  includeArchived?: boolean;
  cursor?: string | undefined;
  limit?: number;
}

/** One department, by id, for the detail panel. */
export function useDepartmentQuery(id: string) {
  return useQuery({
    queryKey: keys.departments.detail(id),
    queryFn: () => api.get<Department>(`/api/v1/departments/${id}`),
    meta: { silent: true },
  });
}

export function useDepartmentsQuery(filters: DepartmentFilters) {
  return useQuery({
    queryKey: keys.departments.list(filters as Record<string, unknown>),
    queryFn: () =>
      api.get<Page<Department>>("/api/v1/departments", {
        query: {
          limit: filters.limit ?? 25,
          includeTotal: true,
          companyId: filters.companyId,
          search: filters.search,
          isActive: filters.isActive,
          includeArchived: filters.includeArchived,
          cursor: filters.cursor,
        },
      }),
    /**
     * Keeps the previous page visible while the next one loads.
     *
     * Without it the table empties on every page change and the layout jumps,
     * which reads as a bug rather than as loading.
     */
    placeholderData: (previous) => previous,
  });
}

/**
 * All departments, unpaginated, for pickers.
 *
 * Deliberately limited to the server's maximum page size rather than walking every
 * page: a company with more than 100 live departments needs a searchable combobox,
 * not a longer dropdown, and silently truncating a select is better than issuing
 * five requests to fill one that nobody can use.
 */
/**
 * Departments for a picker.
 *
 * `enabled` matters: /departments requires `department:read`, which the EMPLOYEE
 * role does not hold. Calling this unconditionally on a page a plain employee can
 * reach produced a 403 and a global error toast on an otherwise working screen —
 * which is what happened on the team calendar. Callers that render the picker
 * conditionally must gate the query the same way.
 */
export function useDepartmentOptions(options?: {
  enabled?: boolean;
  /**
   * Scope to one company.
   *
   * Matters more here than on a list page: a department picker that offers
   * another company's departments lets someone file an employee into an org
   * chart they do not belong to, and nothing downstream would flag it.
   */
  companyId?: string | undefined;
}) {
  return useQuery({
    queryKey: keys.departments.list({ picker: true, companyId: options?.companyId }),
    queryFn: () =>
      api.get<Page<Department>>("/api/v1/departments", {
        query: { limit: 100, isActive: true, companyId: options?.companyId },
      }),
    enabled: options?.enabled ?? true,
    // Departments change rarely; a long stale time avoids refetching this on every
    // form open.
    staleTime: 5 * 60_000,
    select: (page) => page.data,
  });
}

/**
 * Whether a department list covers more than one company.
 *
 * The reason every picker needs to ask: department codes are unique per company
 * rather than across the group, so two businesses legitimately both have an
 * `HR` — and a picker offering "Human Resources (HR)" twice gives an
 * administrator no way to tell them apart, which is how somebody ends up filed
 * into another company's org chart. Naming the company unconditionally would
 * instead repeat one constant value down every row of the single-company view
 * most people use, so it is shown only when it distinguishes something.
 */
export function departmentsSpanCompanies(
  departments: readonly Pick<Department, "company">[],
): boolean {
  return new Set(departments.map((department) => department.company.id)).size > 1;
}

/** Ready-made picker options, disambiguated per {@link departmentsSpanCompanies}. */
export function departmentPickerOptions(
  departments: readonly Department[],
): Array<{ value: string; label: string }> {
  const spans = departmentsSpanCompanies(departments);

  return departments.map((department) => ({
    value: department.id,
    label: spans
      ? `${department.name} (${department.code}) · ${department.company.name}`
      : `${department.name} (${department.code})`,
  }));
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silent: true },
    mutationFn: (input: CreateDepartmentInput) =>
      api.post<Department>("/api/v1/departments", input),
    onSuccess: (department) => {
      // Invalidate the whole subtree: every filtered list and the picker are now
      // stale, and enumerating them here would miss one.
      void queryClient.invalidateQueries({ queryKey: keys.departments.all });
      toast.success(`Department "${department.name}" created.`);
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silent: true },
    mutationFn: ({ id, input }: { id: string; input: UpdateDepartmentInput }) =>
      api.patch<Department>(`/api/v1/departments/${id}`, input),
    onSuccess: (department) => {
      void queryClient.invalidateQueries({ queryKey: keys.departments.all });
      toast.success(`Department "${department.name}" updated.`);
    },
  });
}

export function useArchiveDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silent: true },
    mutationFn: (id: string) => api.delete<Department>(`/api/v1/departments/${id}`),
    onSuccess: (department) => {
      void queryClient.invalidateQueries({ queryKey: keys.departments.all });
      toast.success(`Department "${department.name}" archived.`);
    },
  });
}

export function useRestoreDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silent: true },
    mutationFn: (id: string) =>
      api.post<Department>(`/api/v1/departments/${id}/restore`),
    onSuccess: (department) => {
      void queryClient.invalidateQueries({ queryKey: keys.departments.all });
      toast.success(`Department "${department.name}" restored.`);
    },
  });
}
